import bs58 from "bs58";
import classNames from "classnames";
import { useEffect, useState } from "react";
import {
  IoArrowDownCircleOutline,
  IoArrowUpCircleOutline,
} from "react-icons/io5";
import { RxEnter } from "react-icons/rx";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { RootState } from "reducers";
import { reportError } from "utils";
import {
  LOADER_IDS,
  LoaderName,
  PROGRAM_INFO_BY_ID,
  SYSVAR_IDS,
} from "utils/programs";

type SearchDropdownProps = {
  query: string;
  setSelected: (selected: any) => void;
};

const SearchDropdown = ({ query, setSelected }: SearchDropdownProps) => {
  const { chain } = useSelector((state: RootState) => state.chain);

  const [activeTab, setActiveTab] = useState(0);
  const [options, setOptions] = useState<{ label: string; data: any }[]>([]);

  useEffect(() => {
    // builds and sets local search output
    const builtOptions = buildOptions(query, chain.current_epoch);

    setOptions(builtOptions);

    // clean
    return () => setOptions([]);
  }, [chain.current_epoch, query]);

  useEffect(() => {
    if (options.length > 0) {
      setSelected(options[activeTab]?.data[0]);
    }
  }, [activeTab, options, setSelected]);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex gap-2.5 border-b-[2px] border-gray-100 px-5 pb-2.5 dark:border-lightGrey">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={classNames(
              "btn btn-tab",
              activeTab === index ? "active" : ""
            )}
          >
            {option.label} ({option.data?.length})
          </button>
        ))}
      </div>

      <div className="border-b-[2px] border-gray-100 px-5 dark:border-lightGrey">
        <div className="my-3 grid gap-2">
          <p className="text-lightGrey dark:text-gray-500">
            {`${options[activeTab]?.label} (${options[activeTab]?.data.length})`}
          </p>
          {options[activeTab]?.data.map((opt: any, i: number) => (
            <Link
              className="grid cursor-pointer items-center gap-2 p-2 hover:bg-gray-400 hover:dark:bg-gray-900"
              key={i}
              to={opt.path}
            >
              <span>{opt.value[0]}</span>
              {opt.value[1] && (
                <small className="truncate">{opt.value[1]}</small>
              )}
            </Link>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between px-5 pt-3">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <span className="text-xl">
            <IoArrowUpCircleOutline />
          </span>
          <span className="text-xl">
            <IoArrowDownCircleOutline />
          </span>
          <span>Navigate</span>
          <span className="badge text-gray-700 dark:text-gray-300">ESC</span>
          <span>Close</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <span className="text-xl">
            <RxEnter />
          </span>
          <span>Enter</span>
        </div>
      </div>
    </div>
  );
};

// builds local search options
function buildOptions(rawSearch: string, currentEpoch?: number) {
  const options: any[] = [];

  const search = rawSearch.trim();
  if (search.length === 0) return [];

  const programOptions = buildProgramOptions(search);
  if (programOptions) {
    options.push(programOptions);
  }

  const loaderOptions = buildLoaderOptions(search);
  if (loaderOptions) {
    options.push(loaderOptions);
  }

  const sysvarOptions = buildSysvarOptions(search);
  if (sysvarOptions) {
    options.push(sysvarOptions);
  }

  if (!isNaN(Number(search))) {
    options.push({
      label: "Block",
      data: [
        {
          label: `Block #${search}`,
          value: [search],
          path: `/block/${search}`,
        },
      ],
    });

    if (currentEpoch !== undefined && Number(search) <= currentEpoch + 1) {
      options.push({
        label: "Epoch",
        data: [
          {
            label: `Epoch #${search}`,
            value: [search],
            path: `/epoch/${search}`,
          },
        ],
      });
    }
  }

  // Prefer nice suggestions over raw suggestions
  if (options.length > 0) return options;

  try {
    const decoded = bs58.decode(search);
    if (decoded.length === 32) {
      options.push({
        label: "Address",
        data: [
          {
            label: search,
            value: [search],
            path: "/address/" + search,
          },
        ],
      });
    } else if (decoded.length === 64) {
      options.push({
        label: "Transaction",
        data: [
          {
            label: search,
            value: [search],
            path: "/tx/" + search,
          },
        ],
      });
    }
  } catch (error) {
    reportError(error, {
      search,
    });
  }

  return options;
}

function buildProgramOptions(search: string) {
  const matchedPrograms = Object.entries(PROGRAM_INFO_BY_ID).filter(
    ([address, { name }]) => {
      return (
        name.toLowerCase().includes(search.toLowerCase()) ||
        address.includes(search)
      );
    }
  );

  if (matchedPrograms.length > 0) {
    return {
      label: "Programs",
      data: matchedPrograms.map(([address, { name }]) => ({
        label: name,
        value: [name, address],
        path: "/address/" + address,
      })),
    };
  }
}

const SEARCHABLE_LOADERS: LoaderName[] = [
  "BPF Loader",
  "BPF Loader 2",
  "BPF Upgradeable Loader",
];

function buildLoaderOptions(search: string) {
  const matchedLoaders = Object.entries(LOADER_IDS).filter(
    ([address, name]) => {
      return (
        SEARCHABLE_LOADERS.includes(name) &&
        (name.toLowerCase().includes(search.toLowerCase()) ||
          address.includes(search))
      );
    }
  );

  if (matchedLoaders.length > 0) {
    return {
      label: "Program Loaders",
      data: matchedLoaders.map(([id, name]) => ({
        label: name,
        value: [name, id],
        path: "/address/" + id,
      })),
    };
  }
}

function buildSysvarOptions(search: string) {
  const matchedSysvars = Object.entries(SYSVAR_IDS).filter(
    ([address, name]) => {
      return (
        name.toLowerCase().includes(search.toLowerCase()) ||
        address.includes(search)
      );
    }
  );

  if (matchedSysvars.length > 0) {
    return {
      label: "Sysvars",
      data: matchedSysvars.map(([id, name]) => ({
        label: name,
        value: [name, id],
        path: "/address/" + id,
      })),
    };
  }
}

export default SearchDropdown;
